import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal, Button } from "components";
import { Text } from "components/fields";
import { Checkbox } from "components/fields";
import { Select } from "@paylixecommerce/shared/components";
import { MigrateStore } from "./icons/icons/icons";
import { authApi, formData } from "utils";
import { toastAlert } from "@paylixecommerce/shared/components";
import "./MigrationModal.scss";

const MigrationModal = ({ open, closeModal }) => {
  const getValidationSchema = (platform, step) => {
    if (step === 1) {
      const baseSchema = {
        platform: Yup.string().required("Platform is required"),
        apiKey: Yup.string().required("API Key is required"),
      };

      if (platformsWithShopId.includes(platform)) {
        baseSchema.shopId = Yup.string().required("Shop ID is required");
      }

      return Yup.object().shape(baseSchema);
    }

    return Yup.object().shape({
      migrationValidation: Yup.boolean().test(
        "at-least-one-selected",
        "Please select at least one item to migrate",
        function () {
          const { products, groups, feedback, tos } = this.parent;
          const platformsWithoutTOS = ["sellApp", "sellSN"];
          
          // For platforms without TOS, don't include it in validation
          if (platformsWithoutTOS.includes(platform)) {
            return products || groups || feedback;
          }
          
          return products || groups || feedback || tos;
        }
      ),
    });
  };

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentValidationSchema, setCurrentValidationSchema] = useState(() => getValidationSchema("sellAuth", 1));

  const onSubmit = (values) => {
    if (currentStep === 1) {
      setCurrentStep(2);
      setCurrentValidationSchema(getValidationSchema(values.platform, 2));
    } else {
      setLoading(true);

      const selectedTypes = [];
      if (values.products) selectedTypes.push("products");
      if (values.groups) selectedTypes.push("groups");
      if (values.feedback) selectedTypes.push("feedback");
      if (values.tos) selectedTypes.push("tos");

      const payload = {
        platform: values.platform,
        apiKey: values.apiKey,
        types: JSON.stringify(selectedTypes)
      };

      if (platformsWithShopId.includes(values.platform) && values.shopId) {
        payload.shopId = values.shopId;
      }

      authApi.post('/shops/import', formData(payload))
        .then(res => {
          if (res.status === 200) {
            toastAlert('success', 'Migration completed successfully!');
            closeModal();
            setCurrentStep(1);
            form.resetForm();
          } else {
            throw res;
          }
        })
        .catch(err => {
          const errorMessage = (err && err.data && err.data.message) || 
                              (err && err.message) || 
                              'Migration failed. Please try again.';
          toastAlert('error', errorMessage);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const form = useFormik({
    initialValues: {
      platform: "sellAuth",
      apiKey: "",
      shopId: "",
      products: false,
      groups: false,
      feedback: false,
      tos: false,
      migrationValidation: true,
    },
    validationSchema: currentValidationSchema,
    onSubmit,
  });

  const handlePlatformChange = (selectedOption) => {
    form.setFieldValue("platform", selectedOption.value);
    form.setFieldValue("shopId", "");
    setCurrentValidationSchema(getValidationSchema(selectedOption.value, currentStep));
    form.setFieldError("shopId", "");
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleCancel = () => {
    setCurrentStep(1);
    form.resetForm();
    closeModal();
  };

  const showShopIdField = platformsWithShopId.includes(form.values.platform);

  // Filter migration items based on platform capabilities
  const getAvailableMigrationItems = (platform) => {
    const platformsWithoutTOS = ["sellApp", "sellSN"];
    
    if (platformsWithoutTOS.includes(platform)) {
      return migrationItems.filter(item => item.key !== "tos");
    }
    
    return migrationItems;
  };

  const availableMigrationItems = getAvailableMigrationItems(form.values.platform);

  return (
    <Modal open={open} closeModal={handleCancel} className="migration-modal">
      <div className="paylixecommerce-modal-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center justify-content-center icon-container">
            <MigrateStore className="migrate-icon" />
          </div>
          <div className="header-content">
            <h4 className="title">Migrate From Another Platform</h4>
            <p className="description">
              {currentStep === 1
                ? "This will migrate your store from another platform to Paylix."
                : "Select what you want to migrate from your store."}
            </p>
          </div>
        </div>
        <div className="step-indicator mr-4 sm-mr-0">
          <span className="step-text-full">Step {currentStep} of 2</span>
          <span className="step-text-short">{currentStep}/2</span>
        </div>
      </div>

      <form onSubmit={form.handleSubmit} className="paylixecommerce-modal-body">
        {currentStep === 1 ? (
          <>
            <div className="mb-3">
              <label className="paylixecommerce-label">Platform</label>
              <Select
                options={platformOptions}
                value={platformOptions.find(
                  (option) => option.value === form.values.platform
                )}
                onChange={handlePlatformChange}
                placeholder="Select Platform"
                isSearchable={false}
                className={
                  form.errors.platform && form.touched.platform
                    ? "is-invalid-select"
                    : ""
                }
              />
              {form.errors.platform && form.touched.platform && (
                <div className="invalid-feedback">{form.errors.platform}</div>
              )}
            </div>

            <Text
                name="apiKey"
                label="API Key"
                placeholder="API Key"
                {...form}
            />

            {showShopIdField && (
              <Text
                name="shopId"
                label="Shop ID"
                placeholder="Shop ID"
                {...form}
              />
            )}


          </>
        ) : (
          <>
            <div className="mb-3">
              <label className="paylixecommerce-label">
                What would you like to migrate?
              </label>
              {form.errors.migrationValidation &&
                form.touched.migrationValidation && (
                  <div className="invalid-feedback validation-error">
                    {form.errors.migrationValidation}
                  </div>
                )}
              <div className="migration-items">
                {availableMigrationItems.map((item) => (
                  <div
                    key={item.key}
                    className={`migration-item ${
                      form.values[item.key] ? "selected" : ""
                    }`}
                    onClick={() =>
                      form.setFieldValue(item.key, !form.values[item.key])
                    }
                  >
                    <div className="item-content">
                      <Checkbox name={item.key} label="" {...form} />
                      <div className="item-info">
                        <div className="item-label">{item.label}</div>
                        <div className="item-description">
                          {item.description}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </form>

      <div className="paylixecommerce-modal-footer">
        {currentStep === 2 && (
          <Button
            variant="secondary"
            size="large"
            onClick={handleBack}
            disabled={loading}
          >
            Back
          </Button>
        )}
        <Button
          variant="secondary"
          size="large"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button size="large" onClick={form.handleSubmit} disabled={loading}>
          {loading ? "Migrating..." : currentStep === 1 ? "Next" : "Migrate"}
        </Button>
      </div>
    </Modal>
  );
};

export default MigrationModal;

const platformOptions = [
  { label: "SellAuth", value: "sellAuth" },
  { label: "SellPass", value: "sellPass" },
  //{ label: "BillGang", value: "billGang" },
  //{ label: "SellApp", value: "sellApp" },
  { label: "SellHub", value: "sellHub" }
  //{ label: "SellSN", value: "sellSN" }
];

const migrationItems = [
  {
    key: "products",
    label: "Products",
    description: "Migrate all your products and their details",
  },
  {
    key: "groups",
    label: "Groups",
    description: "Migrate product groups and categories",
  },
  {
    key: "feedback",
    label: "Feedback",
    description: "Migrate customer feedback and reviews",
  },
  {
    key: "tos",
    label: "Terms of Service",
    description: "Migrate your terms of service and policies",
  },
];

const platformsWithShopId = ["billGang", "sellAuth", "sellSN", "sellPass"];
